/* eslint-disable react/require-default-props */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Keyboard,
  KeyboardAvoidingView,
  KeyboardTypeOptions,
  Platform,
  View,
} from 'react-native';
import {ScrollView} from 'react-native-gesture-handler';
import Modal from 'react-native-modal';
import {useDispatch} from 'react-redux';

import Checkbox from '/component/base/Checkbox/Checkbox';
import Icon from '/component/base/Icon';
import FilterRangeInputRadioGroup from '/component/base/RadioButton/FilterRangeInputRadioGroup';
import RadioButtonGroup from '/component/base/RadioButton/RadioButtonGroup';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {FEATURE_FLAGS_CONSTANT} from '/constant/featureFlag.constants';
import {Reports} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {
  getDefaultNewCoachesKey,
  getDefaultNewCoachesValue,
} from '/util/filter.util';
import testProperties from '/util/testing/testProperties';
import {useFeatureFlag} from '/util/useFeatureFlag.util';

import {buildFiltersDraft} from '../filters/builders/modalFilterDraft.util';
import {useClearMyTeamsFilters} from '../hooks/useClearMyTeamsFilters';
import {useMyTeamsContext} from '../hooks/useMyTeamsContext';
import {FilterOptionProps} from '../MyTeamsContent.types';
import reportPayload from '../reportPayload';
import {TabFilters} from '../types/MyTeamsFilters.types';
import {getReportName} from '../util/myTeamsAnalytics.util';
import {buildClearedFilters} from '../util/resetFilters.util';

import {mapRadioOptions} from './modalPopoverFilter.builders';
import {
  selectRadioOption,
  toggleCheckboxOption,
} from './modalPopoverFilter.handlers';
import {applyFiltersFlow} from './modalPopoverFilter.processor';
import {getCheckboxStyle, useModalStyles} from './ModalPopoverFilter.style';

import {useRecordsSorting} from '/context/RecordsSort/useRecordsSorting';

// Define local interface to match component expectations
interface FilterRangeDetails {
  options?: FilterOptionProps[];
  isFilterEnabled?: boolean;
  minDigit: number;
  maxDigit: number;
  resultRangeState: string;
  filterByText: string;
  aboveValue: string;
  belowValue: string;
  isError: boolean;
  ValidationError?: boolean;
  keyboardType: KeyboardTypeOptions;
  isFloatAllowed: boolean;
  isNegativeNumAllowed: boolean;
}
interface MyTeamsFiltersDraft {
  rankOptions: any[];
  fibcOptions: any[];
  newCoachesOptions: any[];
  bonusOptions: any[];

  closeToNextRank: boolean;

  fqvDetails: FilterRangeDetails;
  filterRangeDetails: FilterRangeDetails;
  newCoachesSponsoredDetails: FilterRangeDetails;
}
export interface ModalFilterProps {
  modalVisible: boolean;
  setModalVisible: (visible: boolean) => void;

  applyFilter: boolean;
  setApplyFilter: (value: boolean) => void;

  filters: TabFilters;
  updateFilters: (updates: Partial<TabFilters>) => void;

  userId?: string;
  lastPaidRank?: string;
  highestAchievedRank?: string;
  coachCurrentRank?: string;
}

// Modal Filter Component
export const ModalFilter = ({
  modalVisible,
  setModalVisible,
  applyFilter,
  setApplyFilter,
  filters,
  updateFilters,
  userId = '',
  lastPaidRank = '',
  highestAchievedRank = '',
  coachCurrentRank = '',
}: ModalFilterProps) => {
  const styles = useModalStyles();
  const dispatch = useDispatch();
  const ffTeamsOrgSR = useFeatureFlag(FEATURE_FLAGS_CONSTANT.TEAMS_ORG_SR);
  const {filtersRedux, reduxIsViewTeam, reduxActiveTab} = useMyTeamsContext();

  const clearMyTeamsFilters = useClearMyTeamsFilters();

  const [filtersDraft, setFiltersDraft] = useState<MyTeamsFiltersDraft>(
    () => buildFiltersDraft(filters) as MyTeamsFiltersDraft,
  );

  const [FilterRangeResetTrigger, setFilterRangeResetTrigger] = useState(0);
  const [validateTriggers, setValidateTriggers] = useState({
    fqv: 0,
    filterRange: 0,
    newCoachesSponsored: 0,
  });

  const [FQVDetailsResetTrigger, setFQVDetailsResetTrigger] = useState(0);

  const [validationResults, setValidationResults] = useState({
    fqv: null as boolean | null,
    newCoachesSponsored: null as boolean | null,
    filterRange: null as boolean | null,
  });
  const [pendingValidations, setPendingValidations] = useState(0);

  const [
    NewCoachesSponsoredDetailsResetTrigger,
    setNewCoachesSponsoredDetailsResetTrigger,
  ] = useState(0);

  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);
  const currentY = useRef(0);
  const {tt} = useTranslation('report.myTeams');

  const defaultNewCoachesValue = getDefaultNewCoachesValue(ffTeamsOrgSR);
  const defaultNewCoachesKey = getDefaultNewCoachesKey(ffTeamsOrgSR);

  // Check if records are ready to load
  const {isReadyToLoad} = useRecordsSorting({
    recordsName: reportPayload.reportName,
  });

  // --- Handlers for radio button selection ---
  const handleRadioSelection = (
    key: 'newCoachesOptions' | 'fibcOptions',
    id: string,
  ) => {
    setFiltersDraft(prev => ({
      ...prev,
      [key]: selectRadioOption(prev[key], id),
    }));
  };

  // --- Add onPress handlers to each radio option dynamically ---
  const newCoachesRadioOptions = mapRadioOptions(
    filtersDraft.newCoachesOptions,
    id => handleRadioSelection('newCoachesOptions', id),
  );

  const fibcRadioOptions = mapRadioOptions(filtersDraft.fibcOptions, id =>
    handleRadioSelection('fibcOptions', id),
  );

  const updateCheckboxOptions = (
    key: 'rankOptions' | 'bonusOptions',
    id: string,
  ) => {
    setFiltersDraft(prev => ({
      ...prev,
      [key]: toggleCheckboxOption(prev[key], id),
    }));
  };

  // --- Close modal without applying filters ---
  // Function to close the modal and restore previous filter selections if no filters are applied
  const closeModal = () => {
    setModalVisible(false);
    if (!applyFilter) {
      setFiltersDraft(buildFiltersDraft(filters));
    }
  };

  // Handle validation completion for all filters
  const handleValidationComplete = (
    filterType: 'fqv' | 'newCoachesSponsored' | 'filterRange',
    hasError: boolean,
  ) => {
    setValidationResults({
      ...validationResults,
      [filterType]: hasError,
    });

    setPendingValidations(() => {
      const newCount = pendingValidations - 1;
      if (newCount === 0) {
        // All validations completed, check results
        setValidationResults(currentResults => {
          const allResults = {...currentResults, [filterType]: hasError};
          const hasAnyError = Object.values(allResults)?.some(
            result => result === true,
          );
          if (!hasAnyError) {
            // All validations passed, proceed with applying filters
            applyFiltersAfterValidation();
          }
          return allResults;
        });
      }
      return newCount;
    });
  };

  // Specific handlers for each filter type
  const handleFQVValidationComplete = (hasError: boolean) => {
    handleValidationComplete('fqv', hasError);
  };

  const handleNewCoachesSponsoredValidationComplete = (hasError: boolean) => {
    handleValidationComplete('newCoachesSponsored', hasError);
  };

  const handleFilterRangeValidationComplete = (hasError: boolean) => {
    handleValidationComplete('filterRange', hasError);
  };

  // Apply filters after validation passes
  const applyFiltersAfterValidation = () => {
    setModalVisible(false);
    setApplyFilter(true);

    applyFiltersFlow({
      filtersDraft,
      filtersRedux,
      defaultNewCoachesKey,
      defaultNewCoachesValue,
      updateFilters,
      dispatch,
      clearMyTeamsFilters,
      meta: {
        ffTeamsOrgSR,
        lastPaidRank,
        coachHighestRank: highestAchievedRank,
        coachCurrentRank,
        coachId: userId?.toString?.() || '',
        reportName: getReportName(reduxIsViewTeam, reduxActiveTab),
      },
    });
  };

  // --- Apply filters and store the current selection ---
  // Function that is triggered when "Apply Filters" is pressed
  const handleApplyFiltersPress = () => {
    // Reset validation state
    setValidationResults({
      fqv: null,
      newCoachesSponsored: null,
      filterRange: null,
    });

    // Count enabled filters that need validation
    let validationCount = 0;
    if (
      filtersDraft.fqvDetails?.isFilterEnabled ||
      filtersDraft.newCoachesSponsoredDetails?.isFilterEnabled ||
      filtersDraft.filterRangeDetails?.isFilterEnabled
    ) {
      validationCount++;
    }

    // If no validations needed, proceed directly
    if (validationCount === 0) {
      applyFiltersAfterValidation();
      return;
    }
    // Start parallel validation for all enabled filters
    setPendingValidations(validationCount);
    setValidateTriggers(prev => {
      const next = {...prev};

      if (filtersDraft.fqvDetails?.isFilterEnabled) {
        next.fqv += 1;
      }
      if (filtersDraft.newCoachesSponsoredDetails?.isFilterEnabled) {
        next.newCoachesSponsored += 1;
      }
      if (filtersDraft.filterRangeDetails?.isFilterEnabled) {
        next.filterRange += 1;
      }

      return next;
    });
  };

  useEffect(() => {
    if (modalVisible) {
      setFiltersDraft(buildFiltersDraft(filters));
    }
  }, [modalVisible, filters]);

  // Keyboard event listeners to track keyboard visibility
  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => {
        setKeyboardVisible(true);
        setTimeout(() => {
          if (scrollViewRef.current) {
            scrollViewRef.current.scrollTo({
              y: currentY.current + 50,
              animated: true,
            });
          }
        }, 300);
      },
    );
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        setKeyboardVisible(false);
      },
    );

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  const handleClearFiltersPress = () => {
    setFilterRangeResetTrigger(prev => prev + 1);
    setFQVDetailsResetTrigger(prev => prev + 1);
    setNewCoachesSponsoredDetailsResetTrigger(prev => prev + 1);
    const cleared = buildClearedFilters(filters);

    updateFilters({
      ...cleared,
      frontlineOnly: filtersRedux.frontlineOnly,
    });

    clearMyTeamsFilters(defaultNewCoachesKey, filtersRedux.frontlineOnly);

    setModalVisible(false);
  };

  const onScroll = (event: any) => {
    currentY.current = event.nativeEvent.contentOffset.y;
  };

  const setFQVDetailsDraft = useCallback((value: any) => {
    setFiltersDraft(prev => ({...prev, fqvDetails: value}));
  }, []);

  const setFilterRangeDetailsDraft = useCallback((value: any) => {
    setFiltersDraft(prev => ({...prev, filterRangeDetails: value}));
  }, []);

  const setNewCoachesSponsoredDraft = useCallback((value: any) => {
    setFiltersDraft(prev => ({...prev, newCoachesSponsoredDetails: value}));
  }, []);

  // Return early if data isn't ready to load
  if (!isReadyToLoad) return null;

  const renderContent = () => (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 10 : 0}
      style={Platform.OS === 'web' ? {flex: 1, height: '100%'} : {flex: 1}}>
      <View
        style={styles.modalView}
        {...testProperties(Reports.reportFiltersModal, true)}>
        {/* Scrollable filters */}
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          contentContainerStyle={
            keyboardVisible
              ? styles.scrollContentKeyboard
              : styles.scrollContent
          }
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          onScroll={onScroll}>
          <TouchableOpacity
            {...testProperties(null, true)}
            accessibilityRole="button"
            accessibilityLabel="Filters modal">
            <View style={styles.modalHeader}>
              <Text
                accessibilityLabel="Close"
                onPress={closeModal}
                {...testProperties(Reports.reportFiltersModalClose, true)}>
                <Icon
                  accessible={true}
                  accessibilityLabel="close"
                  name="close"
                />
              </Text>
              <Text
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel="Filters"
                style={styles.headerText}
                {...testProperties(Reports.reportFiltersModalHeader)}
                isBold>
                {tt('filters', 'Filters')}
              </Text>
            </View>

            <View style={styles.radioButtonsContainer}>
              <Text
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel="Ranks"
                style={styles.rankText}
                isBold>
                {tt('rank', 'Ranks')}
              </Text>
              {/* Checkboxes for Rank */}
              {filtersDraft.rankOptions.map((option: any) => (
                <View key={option.id} style={styles.checkboxItem}>
                  <Checkbox
                    accessible={true}
                    accessibilityLabel={`${String(option.text)} : ${
                      option.selected ? 'selected' : ''
                    }`}
                    checkedIconName={
                      option.selected
                        ? 'checkbox-selected'
                        : 'checkbox-unselected'
                    }
                    style={getCheckboxStyle(option.selected)}
                    rectangleCheckbox
                    checked={option.selected}
                    onChange={() =>
                      updateCheckboxOptions('rankOptions', option.id)
                    }
                  />
                  <Text style={styles.checkboxLabel} isBold>
                    {option.text}
                  </Text>
                </View>
              ))}
              {/* Close to Rank Checkbox */}
              {ffTeamsOrgSR && (
                <>
                  <Text style={styles.rankText} isBold>
                    Close to Next Rank
                  </Text>
                  <View style={styles.checkboxItem}>
                    <Checkbox
                      checkedIconName={
                        filtersDraft.closeToNextRank
                          ? 'checkbox-selected'
                          : 'checkbox-unselected'
                      }
                      style={getCheckboxStyle(filtersDraft.closeToNextRank)}
                      rectangleCheckbox
                      checked={filtersDraft.closeToNextRank}
                      onChange={() =>
                        setFiltersDraft(prev => ({
                          ...prev,
                          closeToNextRank: !prev.closeToNextRank,
                        }))
                      }
                    />
                    <Text style={styles.checkboxLabel} isBold>
                      70%+ to next rank
                    </Text>
                  </View>
                </>
              )}
              {/* Radios */}
              <RadioButtonGroup
                title="New Coaches Joined"
                options={newCoachesRadioOptions}
                isBold
              />
              <RadioButtonGroup
                title="FIBC"
                options={fibcRadioOptions}
                isBold
              />
              {ffTeamsOrgSR && (
                <FilterRangeInputRadioGroup
                  Title="Estimated FQV"
                  FilterRangeDetails={filtersDraft.fqvDetails}
                  setFilterRangeDetails={setFQVDetailsDraft}
                  resetTrigger={FQVDetailsResetTrigger}
                  validateTrigger={validateTriggers.fqv}
                  onValidationComplete={handleFQVValidationComplete}
                />
              )}
              {ffTeamsOrgSR && (
                <View>
                  <Text
                    accessible={true}
                    accessibilityLabel={`Bonuses (Last Period)`}
                    style={styles.rankText}
                    isBold>
                    {tt('bonuses', 'Bonuses (Last Period)')}
                  </Text>
                  {/* Checkboxes for Bonuses */}
                  {filtersDraft.bonusOptions.map((option: any) => (
                    <View key={option.id} style={styles.checkboxItem}>
                      <Checkbox
                        accessible={true}
                        accessibilityLabel={`Filter by ${option.text}: ${
                          option.selected ? 'SELECTED' : ''
                        }`}
                        checkedIconName={
                          option.selected
                            ? 'checkbox-selected'
                            : 'checkbox-unselected'
                        }
                        style={getCheckboxStyle(option.selected)}
                        rectangleCheckbox
                        checked={option.selected}
                        onChange={() =>
                          updateCheckboxOptions('bonusOptions', option.id)
                        }
                      />
                      <Text style={styles.checkboxLabel} isBold>
                        {option.text}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
              {ffTeamsOrgSR && (
                <FilterRangeInputRadioGroup
                  Title="New Clients"
                  FilterRangeDetails={filtersDraft.filterRangeDetails}
                  setFilterRangeDetails={setFilterRangeDetailsDraft}
                  resetTrigger={FilterRangeResetTrigger}
                  validateTrigger={validateTriggers.filterRange}
                  onValidationComplete={handleFilterRangeValidationComplete}
                />
              )}
              {ffTeamsOrgSR && (
                <FilterRangeInputRadioGroup
                  Title="New Coaches Acquired"
                  FilterRangeDetails={filtersDraft.newCoachesSponsoredDetails}
                  setFilterRangeDetails={setNewCoachesSponsoredDraft}
                  resetTrigger={NewCoachesSponsoredDetailsResetTrigger}
                  validateTrigger={validateTriggers.newCoachesSponsored}
                  onValidationComplete={
                    handleNewCoachesSponsoredValidationComplete
                  }
                />
              )}
            </View>
          </TouchableOpacity>
        </ScrollView>

        {/* Fixed bottom buttons */}
        <View style={styles.fixedButtonContainer}>
          <TouchableOpacity
            onPress={handleClearFiltersPress}
            style={styles.clearButton}>
            <Text style={styles.clearText} isBold>
              Clear All
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={handleApplyFiltersPress}
            style={styles.applyButton}>
            <Text style={styles.applyText} isBold>
              Apply Filters
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </KeyboardAvoidingView>
  );

  // Modal rendering with filters and actions
  return (
    <Modal
      style={styles.modal}
      isVisible={modalVisible}
      onBackdropPress={closeModal}
      onBackButtonPress={closeModal}
      animationIn={Platform.OS === 'web' ? 'slideInRight' : 'slideInUp'}
      animationOut={Platform.OS === 'web' ? 'slideOutRight' : 'slideOutDown'}>
      {renderContent()}
    </Modal>
  );
};
